<template>
  <div
    ref="tourGuideContentRef"
    class="download-guide-box"
  >
    <ElTour
      v-model="guideVisible"
      :append-to="tourGuideContentRef"
      :mask="{
        color: 'transparent',
      }"
      :close-icon="CloseIcon"
      :scroll-into-view-options="true"
      @close="handleClose"
      @finish="handleFinish"
    >
      <!-- 移动端 -->
      <ElTourStep
        v-if="isMobile"
        :target="props.targetElement"
        placement="top"
        :content-style="diyGuideBoxCss"
      >
        <div>
          <div
            class="download-guide-tips-box"
          >
            <div class="download-guide-tips-icon">
              <img
                src="@/assets/images/share/divider-icon.svg"
                class="download-guide-divider"
                alt="download-guide-divider"
              >
            </div>
            <div class="download-guide-tips-text">
              <i18n-t
                keypath="download-guide-tips-template"
              >
                <template #one>
                  <OpenAppButton>
                    <template #default="slotProps">
                      <span
                        class="primary-color"
                        @click="handleOpenApp(slotProps)"
                      >{{ $t("download-guide-tips-inner") }}</span>
                    </template>
                  </OpenAppButton>
                </template>
              </i18n-t>
            </div>
          </div>

          <el-divider />

          <div
            class="
            download-guide-content"
          >
            <div
              v-for="(item, index) in steps"
              :key="index"
              class="download-guide-box"
            >
              <div class="download-guide-index">
                Step {{ index + 1 }}:
              </div>
              <div class="download-guide-description">
                {{ item }}
              </div>
            </div>

            <div class="download-guide-image">
              <img
                src="@/assets/images/share/download-tips.png"
                alt="download-guide-image"
              >
            </div>
          </div>
        </div>
      </ElTourStep>

      <!-- 桌面端 -->
      <ElTourStep
        v-else
        :target="props.targetElement"
        :placement="pcPlacement"
        :content-style="diyGuideBoxCss"
      >
        <template #header>
          <div class="download-guide-title">
            {{ $t("how-to-download") }}
          </div>
        </template>

        <div class="download-guide-content">
          <div
            v-for="(item, index) in steps"
            :key="index"
            class="download-guide-box"
          >
            <div class="download-guide-index">
              Step {{ index + 1 }}:
            </div>
            <div class="download-guide-description">
              {{ item }}
            </div>
          </div>

          <div class="download-guide-image">
            <img
              src="@/assets/images/share/download-tips.png"
              alt="download-guide-image"
            >
          </div>

          <el-divider>
            <img
              src="@/assets/images/share/divider-icon.svg"
              class="download-guide-divider"
              alt="download-guide-divider"
            >
          </el-divider>

          <div class="download-guide-tips">
            {{ $t("download-guide-tips-1") }}
          </div>

          <!-- 下载按钮 -->
          <DownloadButtons />
        </div>
      </ElTourStep>
    </ElTour>
  </div>
</template>

<script lang="ts" setup>
import CloseIcon from '@/components/icons/CloseIcon.vue'
import DownloadButtons from '@/components/share/DownloadButtons.vue'
import OpenAppButton from '@/components/share/OpenAppButton.vue'
import { useLayout } from '@/store/layout'
import { useShare } from '@/store/share'
import $track from '@/track/webShare'
import { useThrottleFn } from '@vueuse/core'
import {
  ElTour,
  ElTourStep,
} from 'element-plus'
import {
  computed,
  nextTick,
  ref,
  watch,
} from 'vue'
import { useI18n } from 'vue-i18n'

interface TourGuideProps {
  targetElement?: HTMLElement | string;
}

interface TourGuideEmits {
  close: [];
  finish: [];
}

const pcPlacement = ref<'bottom-end' | 'left-end' | 'left' | 'left-start'>('bottom-end');

const { locale, t } = useI18n();

const tourGuideContentRef = ref<HTMLElement>();

// 引导框是否显示
const guideVisible = ref(false);

const props = defineProps<TourGuideProps>();
const emit = defineEmits<TourGuideEmits>();

const shareStore = useShare();

const targetRect = ref<DOMRect | null>(null);

const layoutStore = useLayout();

const isMobile = computed(() => layoutStore.isMobile);

const steps = computed(() => [
  t('download-guide-step-1'),
  t('download-guide-step-2'),
]);

// 自定义引导框样式
const diyGuideBoxCss = computed(() => {
  if (!isMobile.value) {
    return {
      maxWidth: '480px',
    };
  }
  return {
    maxWidth: 'calc(100vw - 32px)',
  };
});

// 获取目标元素的位置和尺寸
const updateTargetRect = async () => {
  let targetEl: HTMLElement | null = null;
  await nextTick();

  if (typeof props.targetElement === 'string') {
    targetEl = document.querySelector(props.targetElement);
  } else if (props.targetElement instanceof HTMLElement) {
    targetEl = props.targetElement;
  }
  if (!targetEl) {
    // 如果目标元素不存在，则隐藏引导框
    shareStore.tourGuideVisible = false;
  } else {
    // 获取目标元素的位置和尺寸
    const targetPos = targetEl.getBoundingClientRect();
    // 计算目标元素底部到页面底部的距离，看看是否塞得下
    const posHeight = targetPos.height + targetPos.top + 625;
    if (posHeight >= window.innerHeight) {
      pcPlacement.value = 'left';
    } else {
      pcPlacement.value = 'bottom-end';
    }
  }
};

// 使用节流的 updateTargetRect 函数，50ms 节流间隔
const throttledUpdateTargetRect = useThrottleFn(updateTargetRect, 50);

const handleClose = () => {
  shareStore.tourGuideVisible = false;
  emit('close');
};

const handleFinish = () => {
  shareStore.tourGuideVisible = false;
  emit('finish');
};

const handleOpenApp = (slotProps: { handleOpenApp: () => void }) => {
  $track.web_share_page_download_guide_click({ button: 'install_app' });
  slotProps.handleOpenApp();
};

// 点击外层关闭 tour 的处理函数
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Element;
  if (target.closest('.el-tour__content') === null) {
    event.preventDefault();
    event.stopPropagation();
    handleClose();
  } else {
    event.stopPropagation();
  }
};

// 监听布局变化，重新渲染
watch(
  () => isMobile.value,
  async () => {
    handleClose()
  },
);
// 监听 tour 显示状态
watch(
  [() => [shareStore.tourGuideVisible], () => locale.value],
  async ([[visible]]) => {
    if (visible) {
      await throttledUpdateTargetRect();
      // 弹窗
      guideVisible.value = true;

      // 延迟 300ms 后监听点击事件
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
        window.addEventListener('resize', throttledUpdateTargetRect);
      }, 300);
    } else {
      guideVisible.value = false;
      // 移除监听事件
      document.removeEventListener('click', handleClickOutside);
      window.addEventListener('resize', throttledUpdateTargetRect);

      targetRect.value = null;
    }
  },
);

</script>

<style lang="less" scoped>
@import "@/assets/styles/screen.less";

.tour-highlight-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 999; // 确保在 Tour 组件之上

  .highlight-svg {
    width: 100%;
    height: 100%;
  }
}

.download-guide-title {
  font-size: 18px;
  font-weight: 600;
  line-height: 22px;
  color: var(--color-primary-text);
  text-align: center;

  .mobile({
    margin-bottom: 16px;
  });
}

.download-guide-tips-box {
  display: flex;
  align-items: flex-start;
  padding-top: 24px;
  .download-guide-tips-icon {
    flex-shrink: 0;
    margin-right: 12px;
    img {
      width: 24px;
      height: 24px;
    }
  }

  .download-guide-tips-text {
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    color: var(--color-secondary-text);
    .primary-color {
      color: var(--color-primary);
    }
  }
}

.download-guide-content {
  font-size: 14px;
  font-weight: 400;
  color: var(--color-primary-text);
  line-height: 22px;

  .mobile({
    line-height: 20px;
  });

  .download-guide-box {
    display: flex;
    align-items: flex-start;
    margin-bottom: 8px;
    .download-guide-index {
      flex-shrink: 0;
      margin-right: 4px;
    }
    .download-guide-description {
      flex-grow: 1;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  .download-guide-image {
    margin-top: 12px;
    padding-bottom: 20px;
    display: flex;
    justify-content: center;
    overflow: hidden;

    .mobile({
      padding-bottom: 20px;
    });

    img {
      width: 320px;
      height: 210px;

      .mobile({
        width: 85%;
        height: auto;
      });
    }
  }

  .download-guide-divider {
    width: 24px;
    height: 24px;
  }

  .download-guide-tips {
    padding-top: 4px;
    padding-bottom: 12px;
    font-size: 14px;
    line-height: 22px;
    font-weight: 400;
    color: var(--color-secondary-text);
    text-align: center;
  }

}
</style>

<style lang="less">
@import "@/assets/styles/screen.less";
.download-guide-box {
  .el-divider--horizontal {
    .mobile({
      margin: 16px 0 !important;
    });
  }

  .el-tour__content {
    padding: 32px 24px 24px !important;
    border-radius: 16px !important;
    border: 1px solid var(--color-primary) !important;
    box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1) !important;
    margin-top: 12px !important;

    .mobile({
      margin-top: -12px !important;
      padding: 16px !important;
      border-radius: 16px !important;
    });

    .el-tour__header {
      padding: 0 0 16px !important;
      .mobile({
        display: none;
      });
    }

    .el-tour__closebtn {
      .pc({
        width: 24px !important;
        height: 24px !important;
        right: 12px !important;
        top: 12px !important;
      });

      .mobile({
        width: 24px !important;
        height: 24px !important;
        right: 8px !important;
        top: 8px !important;
      });
    }

    .el-tour__close {
      font-size: 24px !important;
      .pp-icon {
        color: var(--color-tertiary-text, #98a2ad) !important;
        &:hover {
          color: var(--color-primary) !important;
        }
      }
    }

    .el-tour__arrow {
      border-radius: 2px !important;
      .mobile({
          bottom: -6px;
          border-bottom: 1px solid var(--color-primary) !important;
          border-right: 1px solid var(--color-primary) !important;
      });
    }

    &[data-side="bottom"] {
      .el-tour__arrow {
        .pc({
          top: -6px;
          border-top: 1px solid var(--color-primary) !important;
          border-left: 1px solid var(--color-primary) !important;
        });
      }
    }

    &[data-side="left"] {
      .el-tour__arrow {
        .pc({
          transform: translateY(-10px) rotate(45deg);
          border-top: 1px solid var(--color-primary) !important;
          border-right: 1px solid var(--color-primary) !important;
        });
      }
    }

    .el-tour__footer {
      display: none;
    }
  }
}
</style>
